// Vault unlock / set / destroy UI — single entry point ensureUnlocked()
// gates every aggregator action behind a passphrase prompt. Lives next
// to vault.ts so the two evolve together; everything DOM-specific is
// kept out of vault.ts so the crypto module stays trivially testable.
//
// Flow:
//   1. ensureUnlocked() — call this before any read/write of an
//      aggregator secret.
//   2. If vault.isUnlocked() — returns true immediately, no UI.
//   3. If vault.isInitialized() === false — opens the modal in
//      "set" view (new passphrase + confirm). On success, vault is
//      left unlocked. Resolves true. If the user closes the modal
//      without setting one, resolves false.
//   4. Else — opens the modal in "unlock" view. Resolves true on
//      success, false on cancel.
//   5. The "Forgot passphrase" link inside the unlock view triggers
//      a destructive confirm flow: vault.destroy() runs, then the
//      modal flips to set-view so the user can pick a new passphrase
//      and re-link their banks.
//
// One DOM element gates everything: #vault-modal-backdrop. The two
// child sections #vault-set-view and #vault-unlock-view are toggled
// .hidden based on whether the vault is initialised.
//
// No tests — this is pure DOM glue against the vault.ts API, which
// is the part with the actual logic. Manual test plan:
//   - First visit: clicking "Connect to Plaid" should pop the set-view.
//   - Already initialised + locked: should pop the unlock-view.
//   - Wrong passphrase: status line says "Incorrect passphrase".
//   - Forgot: destroys vault, flips to set-view, allows starting over.

import * as vault from "./vault.js";
import * as log from "../log.js";

// ── DOM accessors ───────────────────────────────────────────────────

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error(`#${id} missing from DOM`);
  return e as T;
}

function setStatus(id: string, text: string, kind: "info" | "error" | "success" = "info"): void {
  const e = el(id);
  e.textContent = text;
  e.classList.remove("error", "success");
  if (kind === "error") e.classList.add("error");
  if (kind === "success") e.classList.add("success");
}

function show(id: string): void { el(id).classList.remove("hidden"); }
function hide(id: string): void { el(id).classList.add("hidden"); }

// ── state for the open modal — at most one prompt in flight ────────

interface PendingPrompt {
  resolve: (unlocked: boolean) => void;
}
let pending: PendingPrompt | null = null;
let wiredOnce = false;

/** Idempotent — first call wires the close/submit/forgot handlers
 *  against the static modal DOM. */
function wire(): void {
  if (wiredOnce) return;
  wiredOnce = true;
  el("vault-modal-close").addEventListener("click", () => closeModal(false));
  el("vault-modal-backdrop").addEventListener("click", (ev) => {
    if (ev.target === ev.currentTarget) closeModal(false);
  });
  document.addEventListener("keydown", (ev) => {
    const ke = ev as KeyboardEvent;
    if (ke.key === "Escape" && !el("vault-modal-backdrop").classList.contains("hidden")) {
      closeModal(false);
    }
  });

  // SET view submit
  el("vault-set-submit").addEventListener("click", onSetSubmit);
  el<HTMLInputElement>("vault-set-confirm").addEventListener("keydown", (ev) => {
    if ((ev as KeyboardEvent).key === "Enter") onSetSubmit();
  });

  // UNLOCK view submit
  el("vault-unlock-submit").addEventListener("click", onUnlockSubmit);
  el<HTMLInputElement>("vault-unlock-pass").addEventListener("keydown", (ev) => {
    if ((ev as KeyboardEvent).key === "Enter") onUnlockSubmit();
  });

  // Forgot passphrase → destroy + flip to set-view
  el("vault-forgot-btn").addEventListener("click", onForgot);
}

async function openModal(): Promise<void> {
  wire();
  const initialised = await vault.isInitialized();
  // Reset inputs + status
  el<HTMLInputElement>("vault-set-pass").value = "";
  el<HTMLInputElement>("vault-set-confirm").value = "";
  el<HTMLInputElement>("vault-unlock-pass").value = "";
  setStatus("vault-set-status", "");
  setStatus("vault-unlock-status", "");

  const title = el("vault-modal-title");
  if (initialised) {
    title.textContent = "Unlock your vault";
    hide("vault-set-view");
    show("vault-unlock-view");
    setTimeout(() => el<HTMLInputElement>("vault-unlock-pass").focus(), 0);
  } else {
    title.textContent = "Set a passphrase";
    show("vault-set-view");
    hide("vault-unlock-view");
    setTimeout(() => el<HTMLInputElement>("vault-set-pass").focus(), 0);
  }
  show("vault-modal-backdrop");
}

function closeModal(unlocked: boolean): void {
  hide("vault-modal-backdrop");
  // Clear the password fields so they don't sit in the DOM after
  // close — minor defence-in-depth against a transient memory dump.
  el<HTMLInputElement>("vault-set-pass").value = "";
  el<HTMLInputElement>("vault-set-confirm").value = "";
  el<HTMLInputElement>("vault-unlock-pass").value = "";
  const p = pending;
  pending = null;
  if (p) p.resolve(unlocked);
}

// ── set view ───────────────────────────────────────────────────────

async function onSetSubmit(): Promise<void> {
  const pass = el<HTMLInputElement>("vault-set-pass").value;
  const confirm = el<HTMLInputElement>("vault-set-confirm").value;
  if (!pass) {
    setStatus("vault-set-status", "Passphrase cannot be empty.", "error");
    return;
  }
  if (pass.length < 8) {
    setStatus("vault-set-status", "Use at least 8 characters. A passphrase (three random words) is better than a password.", "error");
    return;
  }
  if (pass !== confirm) {
    setStatus("vault-set-status", "The two passphrases don't match.", "error");
    return;
  }
  const submit = el<HTMLButtonElement>("vault-set-submit");
  submit.disabled = true;
  setStatus("vault-set-status", "Setting up vault…");
  try {
    await vault.initialize(pass);
    log.info("vault", "Vault initialised on this device.");
    closeModal(true);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    log.error("vault", "Vault initialise failed", { error: msg });
    setStatus("vault-set-status", msg, "error");
  } finally {
    submit.disabled = false;
  }
}

// ── unlock view ────────────────────────────────────────────────────

async function onUnlockSubmit(): Promise<void> {
  const pass = el<HTMLInputElement>("vault-unlock-pass").value;
  if (!pass) {
    setStatus("vault-unlock-status", "Enter your passphrase.", "error");
    return;
  }
  const submit = el<HTMLButtonElement>("vault-unlock-submit");
  submit.disabled = true;
  setStatus("vault-unlock-status", "Unlocking…");
  try {
    await vault.unlock(pass);
    log.info("vault", "Vault unlocked.");
    closeModal(true);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    // Don't log the passphrase, just the failure category.
    log.warn("vault", "Vault unlock failed", { reason: msg });
    setStatus("vault-unlock-status", msg, "error");
  } finally {
    submit.disabled = false;
  }
}

// ── forgot ─────────────────────────────────────────────────────────

async function onForgot(): Promise<void> {
  const ok = window.confirm(
    "Wipe vault?\n\nThis deletes every encrypted aggregator credential on this device. Your bank statements and parsed transactions stay in your data folder — only the connections (Plaid, SimpleFIN) need to be re-linked.\n\nThere's no other recovery path. Continue?",
  );
  if (!ok) return;
  try {
    await vault.destroy();
    log.info("vault", "Vault destroyed by user (forgot passphrase).");
    // Flip to set-view so the user can pick a new passphrase right away.
    const title = el("vault-modal-title");
    title.textContent = "Set a passphrase";
    hide("vault-unlock-view");
    show("vault-set-view");
    el<HTMLInputElement>("vault-set-pass").value = "";
    el<HTMLInputElement>("vault-set-confirm").value = "";
    setStatus("vault-set-status", "Vault wiped. Pick a new passphrase to continue.", "info");
    setTimeout(() => el<HTMLInputElement>("vault-set-pass").focus(), 0);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    log.error("vault", "Vault destroy failed", { error: msg });
    setStatus("vault-unlock-status", msg, "error");
  }
}

// ── public API ─────────────────────────────────────────────────────

/**
 * Make sure the vault is unlocked. If it isn't, prompt the user.
 * Returns true if the vault is now unlocked, false if the user
 * cancelled. Callers should bail out gracefully on false (the user
 * declined — don't pop another modal in their face).
 *
 * Safe to call repeatedly; a no-op when already unlocked.
 */
export function ensureUnlocked(): Promise<boolean> {
  if (vault.isUnlocked()) return Promise.resolve(true);
  if (pending) {
    // Another caller is already waiting on the same modal — chain on.
    return new Promise<boolean>((resolve) => {
      const earlier = pending!.resolve;
      pending!.resolve = (unlocked) => {
        earlier(unlocked);
        resolve(unlocked);
      };
    });
  }
  return new Promise<boolean>((resolve) => {
    pending = { resolve };
    openModal().catch((err) => {
      log.error("vault", "Vault modal open failed", { error: String(err) });
      pending = null;
      resolve(false);
    });
  });
}
